import React from 'react';
import '../styles/PublicPages.css';
import { SchoolLegalHeader, useSchoolLegalBranding } from '../components/SchoolLegalBranding';

const PrivacyPolicy = () => {
  const branding = useSchoolLegalBranding();
  return (
    <div
      className={branding.isSchool ? 'staff-page legal-page legal-page--school' : 'staff-page legal-page'}
      style={branding.style}
    >
      <div className="auth-container">
        <SchoolLegalHeader branding={branding} />

        <div className="auth-form">
          <div className="privacy-content">
            <h1 className="privacy-main-title">Privacy Policy</h1>

            <div className="privacy-intro">
              <p className="privacy-text">
                Homecrowd Inc ("Homecrowd," "we," "us," or "our") provides a platform that allows users to link eligible payment cards, earn points from in-network merchant transactions, and redeem rewards through our mobile application and website (collectively, the "Services"). We are committed to protecting your privacy and being transparent about how we collect and use your information.
              </p>
              <p className="privacy-text">
                This Privacy Policy explains what information we collect, how we use it, and the rights you have regarding your personal data.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">1. Information We Collect</h2>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">1.1 Information You Provide to Us</h3>
                <ul className="privacy-list">
                  <li><span className="list-highlight">Account Information:</span> Name, email, phone number, password or authentication method.</li>
                  <li><span className="list-highlight">Profile Information:</span> School selections, preferences, and optional demographic info you choose to provide.</li>
                  <li><span className="list-highlight">Support Communications:</span> Messages, feedback, or inquiries you submit.</li>
                </ul>
              </div>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">1.2 Information Collected When You Link a Card</h3>
                <p className="privacy-text">
                  We partner with a secure, PCI-compliant third-party card-linking provider (the "Card Linking Provider"). When you choose to link a payment card:
                </p>
                <ul className="privacy-list">
                  <li>We do not receive or store your full card number, CVV, expiration date, or other sensitive card details.</li>
                  <li>The Card Linking Provider encrypts and tokenizes your card credentials.</li>
                  <li>We receive a token and transaction metadata that allows us to identify qualifying, in-network transactions.</li>
                </ul>
              </div>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">1.3 Transaction Data (Points Tracking)</h3>
                <p className="privacy-text">
                  We receive limited transaction information from the Card Linking Provider, including:
                </p>
                <ul className="privacy-list">
                  <li>Merchant name</li>
                  <li>Transaction date and time</li>
                  <li>Transaction amount</li>
                  <li>Masked card identifier (last 4 digits or token)</li>
                </ul>
                <p className="privacy-text">
                  We receive this data only for participating merchants in our network and only to calculate points and rewards. We do not receive item-level purchase data.
                </p>
              </div>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">1.4 Automatically Collected Information</h3>
                <p className="privacy-text">
                  When you use our website or app, we automatically collect:
                </p>
                <ul className="privacy-list">
                  <li>Device information (model, OS, unique device identifiers)</li>
                  <li>Log data (IP address, timestamp, app version)</li>
                  <li>Usage data (pages viewed, buttons clicked)</li>
                  <li>Cookies and similar tracking technologies on the website</li>
                </ul>
                <p className="privacy-text">
                  This is used for security, analytics, and product improvement.
                </p>
              </div>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">2. How We Use Your Information</h2>
              <p className="privacy-text">We use the information we collect to:</p>
              <ul className="privacy-list">
                <li>Create and maintain your account</li>
                <li>Link your payment card securely</li>
                <li>Track qualifying in-network transactions</li>
                <li>Award points and manage your rewards</li>
                <li>Improve and personalize the Services</li>
                <li>Provide customer support</li>
                <li>Prevent fraud or unauthorized activity</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p className="privacy-text privacy-highlight">
                We do not sell your personal information.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">3. How We Share Information</h2>
              <p className="privacy-text">We may share information with:</p>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">3.1 Card Linking Provider</h3>
                <p className="privacy-text">To securely link your card and receive qualifying transaction data.</p>
              </div>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">3.2 Service Providers</h3>
                <p className="privacy-text">Trusted vendors who assist with:</p>
                <ul className="privacy-list">
                  <li>Data storage</li>
                  <li>Payment processing</li>
                  <li>Fraud prevention</li>
                  <li>Analytics</li>
                  <li>Customer support</li>
                  <li>Cloud hosting</li>
                </ul>
                <p className="privacy-text">
                  These providers access data only as needed to perform services for us and under strict confidentiality requirements.
                </p>
              </div>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">3.3 Legal & Compliance</h3>
                <p className="privacy-text">We may disclose information if required to:</p>
                <ul className="privacy-list">
                  <li>Comply with law or regulation</li>
                  <li>Respond to valid legal requests</li>
                  <li>Protect the safety and rights of users, Homecrowd, or the public</li>
                </ul>
              </div>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">3.4 Aggregated & De-Identified Data</h3>
                <p className="privacy-text">
                  We may share statistical or aggregated data that cannot be used to identify you, such as anonymized transaction trends.
                </p>
                <p className="privacy-text privacy-highlight">
                  We do not share or sell personal data to advertisers or data brokers.
                </p>
              </div>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">4. Your Choices & Rights</h2>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">4.1 Access, Update, or Delete Your Information</h3>
                <p className="privacy-text">You may:</p>
                <ul className="privacy-list">
                  <li>Update your profile</li>
                  <li>Disconnect your linked card</li>
                  <li>Request account deletion</li>
                </ul>
                <p className="privacy-text">
                  To make these requests, contact us at: <a href="mailto:support@gethomecrowd.com" className="privacy-link">support@gethomecrowd.com</a>
                </p>
              </div>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">4.2 Marketing Communications</h3>
                <p className="privacy-text">
                  You can opt out of marketing emails at any time by clicking "unsubscribe."
                </p>
              </div>

              <div className="privacy-subsection">
                <h3 className="privacy-subsection-title">4.3 Do Not Track</h3>
                <p className="privacy-text">
                  We do not respond to Do Not Track browser signals.
                </p>
              </div>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">5. Data Security</h2>
              <p className="privacy-text">
                We use reasonable administrative, technical, and physical safeguards to protect your information, including:
              </p>
              <ul className="privacy-list">
                <li>Encrypted transmission (HTTPS)</li>
                <li>Tokenized card-linking</li>
                <li>Limited access controls</li>
                <li>Activity monitoring</li>
                <li>Secure cloud infrastructure</li>
              </ul>
              <p className="privacy-text">
                No method of transmission is 100% secure, but we actively maintain and improve our protections.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">6. Data Retention</h2>
              <p className="privacy-text">We retain:</p>
              <ul className="privacy-list">
                <li>Account information for as long as you maintain an account</li>
                <li>Transaction data only as necessary to operate the rewards program</li>
                <li>Deleted accounts are permanently removed within a reasonable period unless retention is required by law</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">7. Children's Privacy</h2>
              <p className="privacy-text">
                Homecrowd does not knowingly collect or solicit personal information from children under 13. If we learn we have inadvertently collected such information, we will delete it promptly.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">8. International Users (GDPR Notice)</h2>
              <p className="privacy-text">
                If you are located in the European Economic Area or UK, you have the right to:
              </p>
              <ul className="privacy-list">
                <li>Access your data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion</li>
                <li>Withdraw consent</li>
                <li>Request portability</li>
              </ul>
              <p className="privacy-text">Our legal basis for processing includes:</p>
              <ul className="privacy-list">
                <li>Your consent</li>
                <li>Performance of a contract</li>
                <li>Legitimate interests (fraud prevention, service improvement)</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">9. California Residents (CCPA/CPRA)</h2>
              <p className="privacy-text">
                If you are a California resident, you have the right to:
              </p>
              <ul className="privacy-list">
                <li>Know the categories of personal information we collect</li>
                <li>Request deletion of your data</li>
                <li>Opt out of the sale of personal data (we do not sell data)</li>
                <li>Not be discriminated against for exercising these rights</li>
              </ul>
              <p className="privacy-text">
                Requests can be submitted to <a href="mailto:support@gethomecrowd.com" className="privacy-link">support@gethomecrowd.com</a>
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">10. Third-Party Links</h2>
              <p className="privacy-text">
                Our Services may contain links to third-party websites. We are not responsible for their privacy practices. We encourage you to read their policies before interacting with them.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">11. Changes to This Policy</h2>
              <p className="privacy-text">
                We may update this Privacy Policy from time to time. The "Last Updated" date at the top will reflect the latest changes. If changes are material, we will provide notice in the app or via email.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">12. Contact Us</h2>
              <p className="privacy-text">
                If you have questions or requests related to this Privacy Policy, you may contact us at:
              </p>
              <div className="contact-info">
                <p className="privacy-text"><strong>Homecrowd Inc</strong></p>
                <p className="privacy-text">Email: <a href="mailto:support@gethomecrowd.com" className="privacy-link">support@gethomecrowd.com</a></p>
                <p className="privacy-text"><strong>Last updated: January 2026</strong></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;